import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import SearchInput from '@design-system/input/search-input';
import TextBlock from '@component/common/block/text-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import IconBlock from '@component/common/block/icon-block';
import PlanThumbnail from '@component/gridgio/project/plan/plan-thumbnail';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import TextButton from '@design-system/text-button';
import LoadingDots from '@design-system/loading-dots';
import { useSourceInfiniteQuery } from '@hook/gridgio/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';
import Gap from '@component/common/gap';
import Scroll, { ScrollContainer } from '@component/common/scroll';

const PlanThumbnailContainer = styled.div<{ $pinned?: boolean }>`
  width: ${({ $pinned }) => ($pinned ? '75px' : '120px')};
  height: ${({ $pinned }) => ($pinned ? '50px' : '80px')};
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
`;

const PinnedSourceItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
`;
const PinnedSourceItemContainer = styled.div`
  display: flex;
  width: 250px;
  min-height: 50px;
  align-items: center;
  gap: ${size.MARGIN_1};
`;
const PinnedSourceInfoContainer = styled.div`
  display: flex;
  height: 100%;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;
const SourceControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${size.MARGIN_2};
`;
const SearchContainer = styled.div`
  flex: 1;
  min-width: 0;
`;
const EmptyContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${size.MARGIN_2};
`;
const SourceListControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;
const SourceItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;
const SourceItemContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;
const SourceInfoContainer = styled.div`
  display: flex;
  height: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
`;
const SourceActionControlContainer = styled.div`
  display: flex;
  align-self: stretch;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
`;
const SourceActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
`;
const SourceNameContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;
const Home: React.FC = () => {
  const dispatch = useTypedDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sourceQuery = useSourceInfiniteQuery();
  const sources = useMemo(() => sourceQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [], [sourceQuery.data]);
  const pinnedSources = [...(sourceQuery.data?.pages[0]?.data ?? [])]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: (sourceQuery.hasNextPage ?? false) && !sourceQuery.isFetchNextPageError,
    isFetchingNextPage: sourceQuery.isFetchingNextPage,
    fetchNextPage: sourceQuery.fetchNextPage,
    root: scrollContainerRef,
  });
  // TODO: Source별 연결 Work 조회를 연동하고 프로젝트명·작업명·수정 시각을 모달에 표시한다.
  const handleSourceWorkClick = () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }));
  const PinnedSourceItems = pinnedSources.map((source) => (
    <Canvas key={source.key} theme={ETheme.WHITE} padding={size.MARGIN_1} border shadow>
      <PinnedSourceItemContainer>
        <PlanThumbnailContainer $pinned>
          <PlanThumbnail source={source} />
        </PlanThumbnailContainer>
        <PinnedSourceInfoContainer>
          <TextBlock
            text={source.name}
            design={{
              box: { width: '100%', alignX: 'left' },
              text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
            }}
          />
          <IconTextBlock
            icon='right'
            text='연결된 작업'
            arrangement='text-icon'
            design={{
              box: { width: '100%', alignX: 'left', gap: '5px', event: { cursor: 'pointer' } },
              icon: { size: 'tiny', color: color.GRAY_1 },
              text: { size: 'small', color: color.GRAY_1 },
            }}
            click={{ handler: handleSourceWorkClick }}
          />
        </PinnedSourceInfoContainer>
      </PinnedSourceItemContainer>
    </Canvas>
  ));
  const SourceItems = sources.map((source) => (
    <Canvas key={source.key} theme={ETheme.WHITE} width='100%' padding={size.MARGIN_1} border shadow>
      <SourceItemContainer>
        <PlanThumbnailContainer>
          <PlanThumbnail source={source} />
        </PlanThumbnailContainer>
        <Gap width={size.MARGIN_2} />
        <SourceInfoContainer>
          <SourceNameContainer>
            <TextBlock
              text={source.name}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
              }}
            />
            <TextBlock
              text={source.filename}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'small', color: color.BLACK_5, oneline: true },
              }}
            />
          </SourceNameContainer>
          <IconTextBlock
            icon='clock'
            text={new Date(source.createdAt).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' })}
            design={{
              box: { gap: '5px' },
              icon: { size: 'tiny', color: color.GRAY_1 },
              text: { size: 'small', color: color.GRAY_1 },
            }}
          />
        </SourceInfoContainer>
        <SourceActionControlContainer>
          <SourceActionContainer>
            <IconBlock
              icon='pin'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{ handler: () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' })) }}
            />
            <IconBlock
              icon='edit'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{ handler: () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' })) }}
            />
            <IconBlock
              icon='trash'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{ handler: () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' })) }}
            />
          </SourceActionContainer>
          <IconTextBlock
            icon='right'
            text='연결된 작업'
            arrangement='text-icon'
            design={{
              box: { gap: '5px', event: { cursor: 'pointer' } },
              icon: { size: 'small', color: color.GRAY_1 },
              text: { size: 'medium', color: color.GRAY_1 },
            }}
            click={{ handler: handleSourceWorkClick }}
          />
        </SourceActionControlContainer>
      </SourceItemContainer>
    </Canvas>
  ));
  return (
    <Wrapper>
      <TextBlock
        text='원본 도면을 등록하고, 여러 프로젝트의 작업에서 사용하세요.'
        design={{ box: { alignX: 'left' }, text: { size: 'large', color: color.BLACK_5, bold: true } }}
      />
      <Gap height={size.MARGIN_1} />
      <IconTextBlock
        icon='pin'
        text='고정 도면'
        design={{
          box: { alignX: 'left', gap: '5px' },
          icon: { size: 'small', color: color.BLACK_2 },
          text: { size: 'medium', color: color.BLACK_2, bold: true },
        }}
      />
      <Gap height={size.MARGIN_1} />
      <Scroll direction='x'>
        <PinnedSourceItemsContainer>
          {pinnedSources.length > 0 ? (
            PinnedSourceItems
          ) : (
            <Canvas theme={ETheme.WHITE} padding={size.MARGIN_1} border shadow>
              <PinnedSourceItemContainer>
                <TextBlock
                  text='고정된 도면이 없어요.'
                  design={{ box: { width: '100%', alignX: 'center' }, text: { size: 'medium', color: color.GRAY_1 } }}
                />
              </PinnedSourceItemContainer>
            </Canvas>
          )}
        </PinnedSourceItemsContainer>
      </Scroll>
      <Gap height={size.MARGIN_2} />
      <SourceControlContainer>
        <SearchContainer>
          <SearchInput
            value=''
            placeholder='도면명 또는 파일명으로 검색'
            theme={ETheme.CYAN}
            width='100%'
            readOnly
            handleClick={() => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }))}
            handleSearch={() => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }))}
          />
        </SearchContainer>
        <TextButton
          text='도면 등록'
          icon='add-file'
          arrangement='icon-text'
          theme={ETheme.CYAN}
          kind='filled'
          size='medium'
          width='110px'
          shape='pill'
          handleClick={() => dispatch(modalActions.showDialogModal({ type: 'source-create' }))}
        />
      </SourceControlContainer>
      <Gap height={size.MARGIN_2} />
      <SourceListControlContainer>
        {sourceQuery.data && (
          <Chip
            text={`전체 도면 ${sourceQuery.data.pages[0]?.totalCount ?? sources.length}개`}
            size='small'
            theme={ETheme.CYAN}
            kind='outlined'
            shape='round'
            usage='static'
          />
        )}
        <IconBlock
          icon='sort'
          design={{
            box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
            icon: { size: 'small', color: color.CYAN_2 },
          }}
          click={{ handler: () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' })) }}
        />
      </SourceListControlContainer>
      <Gap height={size.MARGIN_1} />
      <ScrollContainer width='100%'>
        <Scroll direction='y' height='100%' containerRef={scrollContainerRef}>
          {sourceQuery.isLoading ? (
            <LoadingDots />
          ) : sourceQuery.isError && sources.length === 0 ? (
            <Canvas theme={ETheme.WHITE} width='100%' padding={size.MARGIN_3} border>
              <TextBlock
                text='도면을 불러오지 못했습니다.'
                design={{ text: { size: 'medium', color: color.GRAY_1 } }}
              />
              <TextButton
                text='다시 시도'
                theme={ETheme.CYAN}
                kind='outlined'
                size='medium'
                handleClick={() => void sourceQuery.refetch()}
              />
            </Canvas>
          ) : sources.length > 0 ? (
            <>
              <SourceItemsContainer>{SourceItems}</SourceItemsContainer>
              <div ref={sentinelRef} style={{ width: '100%', height: '1px', flexShrink: 0 }} />
              {sourceQuery.isFetchingNextPage && <LoadingDots />}
              {sourceQuery.isFetchNextPageError && (
                <>
                  <Gap height={size.MARGIN_1} />
                  <TextBlock
                    text='도면을 더 불러오지 못했습니다.'
                    design={{ text: { size: 'medium', color: color.GRAY_1 } }}
                  />
                  <TextButton
                    text='다시 시도'
                    theme={ETheme.CYAN}
                    kind='outlined'
                    size='medium'
                    handleClick={() => void sourceQuery.fetchNextPage()}
                  />
                </>
              )}
            </>
          ) : (
            <Canvas theme={ETheme.WHITE} width='100%' padding={size.MARGIN_3} border>
              <EmptyContentContainer>
                <IconBlock icon='files' design={{ icon: { size: 'large', color: color.CYAN_2 } }} />
                <TextBlock
                  text='첫 도면을 등록해보세요.'
                  design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
                />
                <TextBlock
                  text='PDF 또는 이미지 파일을 등록할 수 있어요.'
                  design={{ text: { size: 'medium', color: color.GRAY_1 } }}
                />
                <TextButton
                  text='도면 등록'
                  icon='add-file'
                  arrangement='icon-text'
                  theme={ETheme.CYAN}
                  kind='filled'
                  size='medium'
                  width='130px'
                  shape='pill'
                  handleClick={() => dispatch(modalActions.showDialogModal({ type: 'source-create' }))}
                />
              </EmptyContentContainer>
            </Canvas>
          )}
        </Scroll>
      </ScrollContainer>
    </Wrapper>
  );
};
export default Home;
